import { Linkedin, Mail } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const FOUNDER = {
  name: "Tilak Banerjee",
  role: "Founder & Principal Advisor",
  email: "aadibanerjee@gmail.com",
  img: "https://customer-assets-wrfwihn1.emergentagent.net/job_vector-growth-ai/artifacts/9265c6df9762e7e3_image-1.jpeg",
  bio: "Built digital acquisition funnels, paid campaigns, SEO and content plans for European SMEs and tech distributors — growing client sales pipelines by more than 30%. Set up AI-assisted marketing automation and Salesforce CRM pipelines that cut acquisition cost and sped lead-to-opportunity conversion by 22%. Led a commercial market assessment for FIAT Automobile across supply chain workflows, online buying behavior and multichannel retail.",
  facts: [["30%+", "pipeline growth delivered"], ["22%", "faster lead-to-opportunity"], ["FIAT", "enterprise assessment"]],
};

const BENCH = [
  {
    title: "Growth & Paid Media bench",
    d: "Specialist partners for cross-border paid acquisition, SEO architecture and CRO — engaged per mandate.",
    img: "https://images.unsplash.com/photo-1758518727613-00192aed759b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlJTIwY29uc3VsdGluZ3xlbnwwfHx8fDE3OTE1NTc4MDJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "CRM & Automation bench",
    d: "Certified Salesforce and marketing-automation practitioners who build the pipelines, scoring and nurture logic.",
    img: "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlJTIwY29uc3VsdGluZ3xlbnwwfHx8fDE3OTE1NTc4MDJ8MA&ixlib=rb-4.1.0&q=85",
  },
];

export default function Team() {
  return (
    <section className="border-t hairline" data-testid="team-block">
      <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
        <Reveal>
          <Eyebrow>The people</Eyebrow>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            One principal. <span className="text-gradient">A specialist bench.</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,420px)_1fr] lg:gap-16">
          <Reveal>
            <div className="clip-frame relative overflow-hidden border hairline" data-testid="founder-portrait-frame">
              <img
                src={FOUNDER.img}
                alt={`${FOUNDER.name}, ${FOUNDER.role}`}
                className="aspect-[4/5] w-full object-cover object-top"
                data-testid="founder-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display text-2xl font-extrabold tracking-tight text-white" data-testid="founder-name">{FOUNDER.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.26em] text-azure" data-testid="founder-role">{FOUNDER.role}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="flex flex-col justify-center">
            <p className="text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="founder-bio">{FOUNDER.bio}</p>
            <div className="mt-8 grid grid-cols-3 gap-4" data-testid="founder-facts">
              {FOUNDER.facts.map(([k, v]) => (
                <div key={k} className="border-l border-azure/30 pl-4">
                  <p className="font-display text-2xl font-extrabold text-gradient sm:text-3xl">{k}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`mailto:${FOUNDER.email}`}
                data-testid="founder-email-link"
                className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-700 transition-colors hover:border-neon/60 hover:text-neon"
              >
                <Mail className="h-3.5 w-3.5" /> Email Tilak
              </a>
              <a
                href="https://www.linkedin.com/"
                target="_blank"
                rel="noreferrer"
                data-testid="founder-linkedin-link"
                className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-700 transition-colors hover:border-neon/60 hover:text-neon"
              >
                <Linkedin className="h-3.5 w-3.5" /> LinkedIn
              </a>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-5 md:grid-cols-2" data-testid="team-bench">
          {BENCH.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.1}>
              <div className="group flex h-full gap-5 overflow-hidden rounded-2xl border hairline bg-surface/70 p-4 transition-all duration-500 hover:border-neon/40" data-testid={`bench-card-${i}`}>
                <div className="h-28 w-28 shrink-0 overflow-hidden rounded-xl md:h-32 md:w-32">
                  <img src={b.img} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="py-1">
                  <h3 className="font-display text-lg font-bold tracking-tight text-ink">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{b.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
